<template>
    <div class="relative">
        <text-input
            :label="label"
            v-model="flightNumber"
            :name="name"
            :styling="styling"
            @input="onFlightNumberChange"
            @focus="onFocus"
            :error="error"
        ></text-input>
        
        <!-- Flight suggestions dropdown -->
        <div v-if="showSuggestions && flights.length > 0" 
             class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
            <div class="p-2 text-sm text-gray-600 border-b">
                {{ flights.length }} flight{{ flights.length !== 1 ? 's' : '' }} found
            </div>
            <div v-for="flight in flights" 
                 :key="flight.flight_iata + '_' + flight.dep_time"
                 @click="selectFlight(flight)"
                 class="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                <div class="flex justify-between items-start">
                    <div class="flex-1">
                        <div class="font-semibold text-gray-900">
                            {{ flight.flight_iata || flight.flight_number }}
                            <span class="text-sm text-gray-500 ml-2">{{ flight.airline_iata }}</span>
                        </div>
                        <div class="text-sm text-gray-600 mt-1">
                            {{ flight.dep_iata }} → {{ flight.arr_iata }}
                        </div>
                        <div class="text-xs text-gray-500 mt-1">
                            <span v-if="flight.formatted_dep_time">Dep: {{ flight.formatted_dep_time }}</span>
                            <span v-if="flight.formatted_arr_time" class="ml-3">Arr: {{ flight.formatted_arr_time }}</span>
                        </div>
                    </div>
                    <div class="text-xs text-gray-500">
                        <span v-if="flight.status" class="px-2 py-1 rounded-full bg-gray-100">
                            {{ flight.status }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Loading indicator -->
        <div v-if="loading" class="absolute right-3 top-8 transform -translate-y-1/2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
        </div>
        
        <!-- No flights found message -->
        <div v-if="showSuggestions && flights.length === 0 && !loading && hasSearched" 
             class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3">
            <div class="text-sm text-gray-600">
                No flights found for "{{ flightNumber }}" during your booking dates.
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import TextInput from './TextInput.vue';

export default {
    name: 'FlightSearchInput',
    components: {
        TextInput
    },
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            required: true
        },
        name: {
            type: String,
            required: true
        },
        styling: {
            type: String,
            default: ''
        },
        entryTime: {
            type: String,
            default: null
        },
        exitTime: {
            type: String,
            default: null
        },
        airportCode: {
            type: String,
            default: null
        },
        flightType: {
            type: String,
            default: 'inbound', // 'outbound' or 'inbound'
            validator: value => ['outbound', 'inbound'].includes(value)
        },
        error: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            flightNumber: this.modelValue,
            flights: [],
            loading: false,
            showSuggestions: false,
            hasSearched: false,
            searchTimeout: null,
            selectedFlightData: null
        };
    },
    watch: {
        modelValue(newValue) {
            this.flightNumber = newValue;
        },
        flightNumber(newValue) {
            this.$emit('update:modelValue', newValue);
        }
    },
    methods: {
        onFlightNumberChange() {
            this.hasSearched = false;
            this.selectedFlightData = null;
            this.$emit('flight-selected', null);
            
            // Clear existing timeout
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }
            
            // Only search if we have at least 2 characters
            if (this.flightNumber && this.flightNumber.length >= 2) {
                this.searchTimeout = setTimeout(() => {
                    this.searchFlights();
                }, 500); // Debounce for 500ms
            } else {
                this.flights = [];
                this.showSuggestions = false;
            }
        },
        
        onFocus() {
            if (this.flights.length > 0) {
                this.showSuggestions = true;
            }
        },
        
        async searchFlights() {
            if (!this.flightNumber || this.flightNumber.length < 2) {
                return;
            }

            this.loading = true;
            this.hasSearched = true;

            try {
                const params = {
                    flight_number: this.flightNumber,
                    entry_time: this.entryTime,
                    airport_code: this.airportCode
                };

                // exitTime is optional — for outbound-only searches we may not have it
                if (this.exitTime) {
                    params.exit_time = this.exitTime;
                } else {
                    // Use entry_time as exit_time fallback so the API validation passes
                    params.exit_time = this.entryTime;
                }

                const response = await axios.post('/api/flights/search', params);

                if (response.data.success) {
                    this.flights = response.data.flights;
                    this.showSuggestions = true;
                }
            } catch (error) {
                console.error('Flight search error:', error);
                this.flights = [];
            } finally {
                this.loading = false;
            }
        },

        selectFlight(flight) {
            this.flightNumber = flight.flight_iata || flight.flight_number || '';
            this.selectedFlightData = flight;
            this.showSuggestions = false;
            this.$emit('flight-selected', flight);

            // For outbound flights, emit the destination so inbound can be loaded
            if (this.flightType === 'outbound' && flight.arr_iata) {
                this.$emit('destination-changed', flight.arr_iata);
            }
        },
        
        // Close suggestions when clicking outside
        handleClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.showSuggestions = false;
            }
        }
    },
    
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
    }
};
</script>

<style scoped>
.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>